import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { FileText, Printer, Calendar, Package, CreditCard, Eye } from 'lucide-react';
import { receiptAPI } from '../utils/api';
import toast from 'react-hot-toast';

const ReceiptHistory = forwardRef(({ customerId, customerName }, ref) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fiş geçmişini yükle
  const fetchReceiptHistory = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Fiş geçmişi yükleniyor, customerId:', customerId);
      const response = await receiptAPI.getByCustomerId(customerId);
      console.log('📡 API Response:', response);
      
      // Backend doğrudan array döndürüyor, content alanı ile
      if (Array.isArray(response.data)) {
        console.log('✅ Array response alındı, length:', response.data.length);
        // Raw data'yı parse et - content field'ı ile
        const receipts = response.data.map(receipt => ({
          id: receipt.id,
          type: receipt.type,
          referenceId: receipt.reference_id,
          customerId: receipt.customer_id,
          customerName: receipt.customer_name,
          documentNumber: receipt.document_number,
          printedAt: receipt.printed_at,
          createdAt: receipt.created_at || receipt.printed_at,
          // Content alanını string olarak saklıyoruz - parse işlemi sonra yapılacak
          content: receipt.content || '{}'
        }));
        
        console.log('📋 Parsed receipts:', receipts);
        setReceipts(receipts);
      } else {
        console.log('❌ Response is not array:', response.data);
        setReceipts([]);
      }
    } catch (error) {
      console.error('❌ Fiş geçmişi yükleme hatası:', error);
      toast.error('❌ Fiş geçmişi yüklenemedi.', {
        duration: 4000,
        position: 'top-right',
      });
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Parent component'ten çağrılabilir refresh fonksiyonu
  useImperativeHandle(ref, () => ({
    refresh: fetchReceiptHistory
  }));

  useEffect(() => {
    if (customerId) {
      fetchReceiptHistory();
    }
  }, [customerId, fetchReceiptHistory]);

  // Tarih formatı
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fiş tipine göre ikon
  const getReceiptIcon = (type) => {
    switch (type) {
      case 'delivery':
        return <Package className="w-4 h-4 text-blue-600" />;
      case 'batch_delivery':
        return <Package className="w-4 h-4 text-purple-600" />;
      case 'payment':
        return <CreditCard className="w-4 h-4 text-green-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  // Fiş tipine göre açıklama
  const getReceiptTypeLabel = (type) => {
    switch (type) {
      case 'delivery':
        return 'Teslimat Fişi';
      case 'batch_delivery':
        return 'Toplu Teslimat Fişi';
      case 'payment':
        return 'Ödeme Makbuzu';
      default:
        return 'Bilinmeyen Tip';
    }
  };

  // Fiş detayını göster - ARTIK PREVIEW AÇACAK
  const showReceiptDetail = (receipt) => {
    showReceiptPreview(receipt); // JSON modal yerine fiş önizlemesi aç
  };

  // Fiş yeniden yazdır (ORİJİNAL fişin birebir aynısı)
  const reprintReceipt = async (receipt) => {
    try {
      console.log('🖨️ Fiş yazdırılıyor:', receipt);
      console.log('🖨️ Receipt content (raw):', receipt.content);
      
      // Eğer content null, undefined veya boş string ise
      if (!receipt.content || receipt.content === '{}' || receipt.content === 'null') {
        console.error('❌ Content alanı boş:', receipt.content);
        toast.error('❌ Bu fiş için detaylı veri bulunamıyor. Backend content alanını dönmüyor.', {
          duration: 5000,
          position: 'top-right',
        });
        return;
      }
      
      // Content'i parse et
      let contentData;
      try {
        contentData = typeof receipt.content === 'string' ? JSON.parse(receipt.content) : receipt.content;
        console.log('🖨️ Parsed content:', contentData);
      } catch (e) {
        console.error('❌ Content parse hatası:', e);
        toast.error('❌ Fiş verisi bozuk, yazdırılamıyor', {
          duration: 3000,
          position: 'top-right',
        });
        return;
      }
      
      const printWindow = window.open('', '', 'width=300,height=600');
      
      let htmlContent = '';
      
      if (receipt.type === 'delivery') {
        htmlContent = generateExactDeliveryReceiptHTML(receipt, contentData);
      } else if (receipt.type === 'batch_delivery') {
        htmlContent = generateExactBatchDeliveryReceiptHTML(receipt, contentData);
      }
      
      console.log('🖨️ Generated HTML length:', htmlContent.length);
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 100);
      
      toast.success('📄 Fiş yazdırıldı', {
        duration: 2000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('❌ Fiş yazdırma hatası:', error);
      toast.error('❌ Fiş yazdırılamadı', {
        duration: 3000,
        position: 'top-right',
      });
    }
  };

  // Fiş önizlemesi göster (modal'da)
  const showReceiptPreview = (receipt) => {
    console.log('👁️ Preview gösteriliyor:', receipt);
    console.log('📦 Raw content:', receipt.content);
    console.log('📦 Content type:', typeof receipt.content);
    
    // Eğer content null, undefined veya boş string ise
    if (!receipt.content || receipt.content === '{}' || receipt.content === 'null') {
      console.error('❌ Content alanı boş:', receipt.content);
      toast.error('❌ Bu fiş için detaylı veri bulunamıyor. Backend content alanını dönmüyor.', {
        duration: 5000,
        position: 'top-right',
      });
      return;
    }
    
    let contentData;
    try {
      contentData = typeof receipt.content === 'string' ? JSON.parse(receipt.content) : receipt.content;
      console.log('✅ Parsed content data:', contentData);
    } catch (e) {
      console.error('❌ Preview content parse hatası:', e);
      toast.error('❌ Fiş verisi bozuk, önizleme gösterilemiyor', {
        duration: 3000,
        position: 'top-right',
      });
      return;
    }
    
    const previewWindow = window.open('', '', 'width=350,height=600');
    
    let htmlContent = '';
    
    if (receipt.type === 'delivery') {
      htmlContent = generateExactDeliveryReceiptHTML(receipt, contentData);
    } else if (receipt.type === 'batch_delivery') {
      htmlContent = generateExactBatchDeliveryReceiptHTML(receipt, contentData);
    }
    
    // Önizleme için ekstra stil ekle
    htmlContent = htmlContent.replace(
      '<body>',
      '<body style="background: #f5f5f5; padding: 20px;">'
    );
    
    previewWindow.document.write(htmlContent);
    previewWindow.document.close();
    previewWindow.focus();
  };

  // ORİJİNAL DeliveryReceipt component'inin BIREBIR aynı HTML template'i
  const generateExactDeliveryReceiptHTML = (receipt, contentData) => {
    const delivery = contentData.delivery || {};
    const customer = contentData.customer || {};
    
    console.log('🛠️ Building delivery HTML with:', { delivery, customer });
    
    return `
      <html>
        <head>
          <title>Teslimat Fişi - ${customer.name || receipt.customerName}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Roboto", "Helvetica Neue", Arial, sans-serif;
              font-size: 11px;
              line-height: 1.4;
              margin: 0;
              padding: 0;
              width: 280px;
              color: #000;
            }
            .receipt-print {
              width: 280px;
              padding: 8px;
              text-align: left;
            }
            .company-info {
              text-align: center;
              margin-bottom: 8px;
              font-size: 10px;
              line-height: 1.2;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .solid-line { border-bottom: 1px solid #000; margin: 6px 0; }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin: 2px 0;
              font-size: 10px;
            }
            .signature-area { margin-top: 15px; font-size: 10px; }
            .signature-line {
              border-bottom: 1px solid #000;
              display: inline-block;
              width: 120px;
              margin-left: 5px;
            }
            .footer-info {
              text-align: center;
              font-size: 9px;
              margin-top: 10px;
              color: #666;
            }
            p { margin: 2px 0; }
            h3 { margin: 6px 0; font-size: 12px; }
            @media print {
              body {
                width: 280px !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              .receipt-print {
                width: 280px !important;
                padding: 8px !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-print">
            <!-- Logo placeholder -->
            <div style="text-align: center; margin-bottom: 12px;">
              <div style="margin: 0 auto; max-width: 150px; max-height: 60px; display: block;"></div>
            </div>
            
            <!-- Company Info -->
            <div class="company-info">
              <div class="bold">EMANET TAKİP SİSTEMİ</div>
            </div>
            
            <!-- Header -->
            <h3 class="center bold">TESLİMAT BELGESİ</h3>
            
            <div class="solid-line"></div>
            
            <!-- Document Info - ORİJİNAL FORMAT -->
            <div class="info-row">
              <span class="bold">BELGE NO</span>
              <span>: #${delivery.id ? delivery.id.toString().padStart(6, '0') : receipt.documentNumber}</span>
            </div>
            <div class="info-row">
              <span class="bold">MÜŞTERİ</span>
              <span>: ${customer.name || receipt.customerName}</span>
            </div>
            <div class="info-row">
              <span class="bold">TARİH</span>
              <span>: ${formatDate(delivery.date || receipt.printedAt)}</span>
            </div>
            
            <div class="solid-line"></div>
            
            <!-- Product Info -->
            <div class="info-row">
              <span class="bold">ÜRÜN</span>
              <span>: ${delivery.productName || 'Teslimat'}</span>
            </div>
            <div class="info-row">
              <span class="bold">MİKTAR</span>
              <span>: ${delivery.quantity || 1} ${delivery.unit || 'adet'}</span>
            </div>
            
            ${delivery.note ? `
            <div style="margin: 4px 0; font-size: 9px;">
              <span class="bold">NOT: </span>
              <span>${delivery.note}</span>
            </div>
            ` : ''}
            
            <div class="solid-line"></div>
            
            <!-- Signatures -->
            <div class="signature-area">
              <p>Teslim Eden: <span class="signature-line"></span></p>
              <br />
              <p>Teslim Alan: <span class="signature-line"></span></p>
            </div>
            
            <!-- Footer -->
            <div class="footer-info">
              <div>${formatDate(delivery.date || receipt.printedAt)} tarihinde yazdırılmıştır.</div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  // ORİJİNAL DeliveryBatchReceipt component'inin BIREBIR aynı HTML template'i
  const generateExactBatchDeliveryReceiptHTML = (receipt, contentData) => {
    const deliveries = contentData.deliveries || [];
    const customer = contentData.customer || {};
    const totalDeliveries = deliveries.length;
    const totalQuantity = deliveries.reduce((total, d) => total + parseFloat(d.quantity || 0), 0);
    
    console.log('🛠️ Building batch HTML with:', { deliveries, customer, totalDeliveries, totalQuantity });
    
    return `
      <html>
        <head>
          <title>Toplu Teslimat Fişi - ${customer.name || receipt.customerName}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Roboto", "Helvetica Neue", Arial, sans-serif;
              font-size: 11px;
              line-height: 1.4;
              margin: 0;
              padding: 0;
              width: 280px;
              color: #000;
            }
            .receipt-print {
              width: 280px;
              padding: 8px;
              text-align: left;
            }
            .company-info {
              text-align: center;
              margin-bottom: 8px;
              font-size: 10px;
              line-height: 1.2;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .solid-line { border-bottom: 1px solid #000; margin: 6px 0; }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin: 2px 0;
              font-size: 10px;
            }
            .table-header {
              display: flex;
              background-color: #f0f0f0;
              padding: 2px 0;
              border-bottom: 1px solid #ccc;
              font-weight: bold;
              font-size: 9px;
            }
            .table-row {
              display: flex;
              padding: 2px 0;
              border-bottom: 1px solid #eee;
              font-size: 9px;
            }
            .signature-area { margin-top: 15px; font-size: 10px; }
            .signature-line {
              border-bottom: 1px solid #000;
              display: inline-block;
              width: 120px;
              margin-left: 5px;
            }
            .footer-info {
              text-align: center;
              font-size: 9px;
              margin-top: 10px;
              color: #666;
            }
            p { margin: 2px 0; }
            h3 { margin: 6px 0; font-size: 12px; }
            @media print {
              body {
                width: 280px !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              .receipt-print {
                width: 280px !important;
                padding: 8px !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-print">
            <!-- Logo placeholder -->
            <div style="text-align: center; margin-bottom: 12px;">
              <div style="margin: 0 auto; max-width: 150px; max-height: 60px; display: block;"></div>
            </div>
            
            <!-- Company Info - ORİJİNAL İKİ SATIR -->
            <div class="company-info">
              <div class="bold">EMANET TAKİP SİSTEMİ</div>
              <div>TESLİMAT YÖNETİM SİSTEMİ</div>
            </div>
            
            <!-- Header -->
            <h3 class="center bold">TESLİMAT BELGESİ</h3>
            
            <div class="solid-line"></div>
            
            <!-- Document Info - ORİJİNAL BELGE NO FORMAT -->
            <div class="info-row">
              <span class="bold">BELGE NO</span>
              <span>: ${receipt.documentNumber}</span>
            </div>
            <div class="info-row">
              <span class="bold">MÜŞTERİ</span>
              <span>: ${customer.name || receipt.customerName}</span>
            </div>
            <div class="info-row">
              <span class="bold">TARİH</span>
              <span>: ${formatDate(receipt.printedAt)}</span>
            </div>
            
            <div class="solid-line"></div>
            
            <!-- Deliveries Table Header - ORİJİNAL WIDTH'LER -->
            <div class="table-header">
              <span style="width: 40px;">S.No</span>
              <span style="width: 120px;">ÜRÜN</span>
              <span style="width: 60px;">MİKTAR</span>
              <span style="width: 50px;">BİRİM</span>
            </div>
            
            <!-- Deliveries - HER DELIVERY İÇİN ROW VE NOT -->
            ${deliveries.map((delivery, index) => `
              <div>
                <div class="table-row">
                  <span style="width: 40px;">${index + 1}</span>
                  <span style="width: 120px; font-size: 8px;">${delivery.productName || 'Ürün'}</span>
                  <span style="width: 60px;">${delivery.quantity || 0}</span>
                  <span style="width: 50px;">${delivery.unit || 'adet'}</span>
                </div>
                ${delivery.note ? `
                <div style="margin: 1px 0 3px 40px; font-size: 8px; font-style: italic;">
                  Not: ${delivery.note}
                </div>
                ` : ''}
              </div>
            `).join('')}
            
            <div class="solid-line"></div>
            
            <!-- Summary - SADECE TOPLAM TESLİMAT -->
            <div class="info-row bold">
              <span>TOPLAM TESLİMAT</span>
              <span>: ${totalDeliveries} ADET</span>
            </div>
            
            <div class="solid-line"></div>
            
            <!-- Signatures -->
            <div class="signature-area">
              <p>Teslim Eden: <span class="signature-line"></span></p>
              <br />
              <p>Teslim Alan: <span class="signature-line"></span></p>
            </div>
            
            <!-- Footer -->
            <div class="footer-info">
              <div>${formatDate(receipt.printedAt)} tarihinde yazdırılmıştır.</div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center space-x-3">
          <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Fiş geçmişi yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Fiş Geçmişi ({receipts.length})
        </h3>
      </div>

      {/* Receipt List */}
      {receipts.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900">Henüz fiş kaydı yok</h3>
          <p className="text-gray-500">Yazdırılan fişler burada görünecek.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {receipts.map((receipt) => (
            <div key={receipt.id} className="bg-gray-50 rounded-apple p-4 hover:bg-gray-100 transition-colors">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    {getReceiptIcon(receipt.type)}
                    <span className="font-semibold text-gray-900">
                      {receipt.documentNumber}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-apple text-xs font-medium bg-primary-100 text-primary-800">
                      {getReceiptTypeLabel(receipt.type)}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-3 h-3" />
                      <span>Yazdırma: {formatDate(receipt.printedAt)}</span>
                    </div>
                    
                    {receipt.type === 'delivery' && (
                      <div>
                        {(() => {
                          try {
                            const content = typeof receipt.content === 'string' ? JSON.parse(receipt.content) : receipt.content;
                            return `Ürün: ${content?.delivery?.productName || 'Ürün'} - ${content?.delivery?.quantity || 1} ${content?.delivery?.unit || 'adet'}`;
                          } catch (e) {
                            return 'Teslimat bilgisi';
                          }
                        })()}
                      </div>
                    )}
                    
                    {receipt.type === 'batch_delivery' && (
                      <div>
                        {(() => {
                          try {
                            const content = typeof receipt.content === 'string' ? JSON.parse(receipt.content) : receipt.content;
                            return `Toplam: ${content?.totalDeliveries || 0} teslimat, ${content?.totalQuantity || 0} miktar`;
                          } catch (e) {
                            return 'Toplu teslimat bilgisi';
                          }
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => showReceiptDetail(receipt)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                    title="Detayları Görüntüle"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => reprintReceipt(receipt)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                    title="Yeniden Yazdır"
                  >
                    <Printer size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default ReceiptHistory; 